import { motion } from 'framer-motion';
import { Target, TrendingUp } from 'lucide-react';

/**
 * Macro Tracker Component
 * Visual macro tracking with charts and daily targets
 */
export default function MacroTracker({ dailyMacros, targets = null }) {
  // Default targets if not provided
  const defaultTargets = {
    calories: 2000,
    protein: 150,
    carbs: 200,
    fats: 65,
  };

  const macroTargets = targets || defaultTargets;

  const calculatePercentage = (current, target) => {
    if (!target || target === 0) return 0;
    return Math.min(100, Math.round((current / target) * 100));
  };

  const getMacroColor = (macro) => {
    switch (macro) {
      case 'calories':
        return 'bg-blue-500';
      case 'protein':
        return 'bg-red-500';
      case 'carbs':
        return 'bg-yellow-500';
      case 'fats':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center gap-2 mb-6">
        <Target className="w-6 h-6 text-primary-600" />
        <h2 className="text-2xl font-bold text-gray-900">Daily Macros</h2>
      </div>

      {/* Macro Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {/* Calories */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-blue-900">Calories</p>
            <TrendingUp className="w-4 h-4 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-blue-900">
            {dailyMacros.calories || 0}
          </p>
          <p className="text-xs text-blue-700 mt-1">
            of {macroTargets.calories} target
          </p>
          <div className="mt-2 w-full bg-blue-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${calculatePercentage(dailyMacros.calories, macroTargets.calories)}%`,
              }}
            ></div>
          </div>
        </div>

        {/* Protein */}
        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-red-900">Protein</p>
            <TrendingUp className="w-4 h-4 text-red-600" />
          </div>
          <p className="text-2xl font-bold text-red-900">
            {dailyMacros.protein || 0}g
          </p>
          <p className="text-xs text-red-700 mt-1">
            of {macroTargets.protein}g target
          </p>
          <div className="mt-2 w-full bg-red-200 rounded-full h-2">
            <div
              className="bg-red-600 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${calculatePercentage(dailyMacros.protein, macroTargets.protein)}%`,
              }}
            ></div>
          </div>
        </div>

        {/* Carbs */}
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-yellow-900">Carbs</p>
            <TrendingUp className="w-4 h-4 text-yellow-600" />
          </div>
          <p className="text-2xl font-bold text-yellow-900">
            {dailyMacros.carbs || 0}g
          </p>
          <p className="text-xs text-yellow-700 mt-1">
            of {macroTargets.carbs}g target
          </p>
          <div className="mt-2 w-full bg-yellow-200 rounded-full h-2">
            <div
              className="bg-yellow-600 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${calculatePercentage(dailyMacros.carbs, macroTargets.carbs)}%`,
              }}
            ></div>
          </div>
        </div>

        {/* Fats */}
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-green-900">Fats</p>
            <TrendingUp className="w-4 h-4 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-green-900">
            {dailyMacros.fats || 0}g
          </p>
          <p className="text-xs text-green-700 mt-1">
            of {macroTargets.fats}g target
          </p>
          <div className="mt-2 w-full bg-green-200 rounded-full h-2">
            <div
              className="bg-green-600 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${calculatePercentage(dailyMacros.fats, macroTargets.fats)}%`,
              }}
            ></div>
          </div>
        </div>
      </div>

      {/* Macro Distribution Pie Chart (Visual Representation) */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <p className="text-sm font-medium text-gray-700 mb-3">Macro Distribution</p>
        <div className="flex items-center justify-center gap-2">
          {dailyMacros.protein > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500 rounded"></div>
              <span className="text-xs text-gray-600">
                Protein: {Math.round((dailyMacros.protein * 4 / (dailyMacros.calories || 1)) * 100)}%
              </span>
            </div>
          )}
          {dailyMacros.carbs > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-yellow-500 rounded"></div>
              <span className="text-xs text-gray-600">
                Carbs: {Math.round((dailyMacros.carbs * 4 / (dailyMacros.calories || 1)) * 100)}%
              </span>
            </div>
          )}
          {dailyMacros.fats > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded"></div>
              <span className="text-xs text-gray-600">
                Fats: {Math.round((dailyMacros.fats * 9 / (dailyMacros.calories || 1)) * 100)}%
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
